import React from 'react';
import ReactDOM from 'react-dom';
import { Handle, Position } from 'reactflow';
import { useParams } from 'react-router-dom';
import { Database, Archive, CheckCircle, XCircle, Clock, Trash2, Sparkles, ExternalLink } from 'lucide-react';
import { NodeData } from '../../../types';
import { useProjectStore, useFlowStore } from '../../../store';
import { useContextualNavigation } from '../../../hooks/useContextualNavigation';
import ModelDetailsModal from '../../models/ModelDetailsModal';
import ConfirmationDialog from '../../common/ConfirmationDialog';
import Tooltip from '../../common/Tooltip';
import { NodeLabel, NodeBadge } from './shared';
import { useEntityEvent } from '../../../hooks/useEntityEvent';
import { useQueue, clearCachedQueue } from '../../../hooks/useQueue';
import { useDeleteStep } from '../../../hooks/queries/useStepMutations';
import { useModels } from '../../../hooks/queries/useModels';

interface QueueWriteNodeProps {
  data: NodeData;
  selected: boolean;
}

const QueueWriteNode: React.FC<QueueWriteNodeProps> = ({ data, selected }) => {
  // AC-1412 / AC-1377 P5.1: project models flow through React Query
  // (`useModels`) instead of `useProjectStore.models`.
  const currentProjectId = useProjectStore((state) => state.currentProject?.project_id);
  const modelsQuery = useModels(currentProjectId);
  const models = React.useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const { nodes, edges, setNodes, setEdges, setSelectedNode, selectedNode, isModelModalOpen, setIsModelModalOpen } = useFlowStore();

  // AC-1406: Delete via React Query mutation hook.
  const deleteStepMutation = useDeleteStep(data.step?.orchestration_id ?? '');
  const { navigateWithContext } = useContextualNavigation();
  const { projectId } = useParams<{ projectId: string }>();
  const [isDeletable, setIsDeletable] = React.useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  // Handler to open queue details page
  const handleOpenQueue = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent node selection
    if (queue?.queue_id && projectId) {
      navigateWithContext(`/projects/${projectId}/queues/${queue.queue_id}`, 'Workflow');
    }
  };

  // Check if node can be deleted (not referenced by other nodes)
  React.useEffect(() => {
    if (!data.step || !data.step.orchestration_step_id) {
      setIsDeletable(false);
      return;
    }
    const canDelete = !nodes.some(
      n =>
        n.id !== data.id &&
        n.data?.step?.previous_step_id &&
        data.step?.orchestration_step_id &&
        String(n.data.step.previous_step_id) === String(data.step.orchestration_step_id)
    );
    setIsDeletable(canDelete);
  }, [nodes, data.id, data.step?.orchestration_step_id]);

  // Read the configured target queue id once - both the mount fetch and the
  // entity-event invalidation listener need it.
  const targetQueueId = (data.step?.config as { target_queue_id?: number } | undefined)?.target_queue_id;

  const { queue, isLoading: isLoadingQueue } = useQueue(targetQueueId);

  // AC-1369: clear the cached queue immediately when the referenced queue is
  // deleted elsewhere (e.g. the user deletes it from the Queues page). Without
  // this the node keeps displaying the stale name until something else
  // triggers a re-render.
  useEntityEvent({
    entity: 'queue',
    id: targetQueueId,
    handler: (event) => {
      if (event.kind === 'deleted' && targetQueueId !== undefined) {
        clearCachedQueue(targetQueueId);
      }
    },
  });

  const handleDelete = async () => {
    if (!data.step?.orchestration_step_id) return;

    try {
      await deleteStepMutation.mutateAsync({
        stepId: data.step.orchestration_step_id,
      });

      // Remove the node and its connected edges from local canvas state
      // for an immediate UX response. The mutation hook's invalidation
      // refreshes the steps cache; the seed effect will rebuild from it.
      setNodes(nodes.filter(node => node.id !== data.id));
      setEdges(edges.filter(edge => edge.source !== data.id && edge.target !== data.id));

      setSelectedNode(null);
    } catch (error) {
      console.error('Failed to delete step:', error);
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'running':
        return <Clock className="w-4 h-4 text-primary-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return 'border-blue-500 bg-blue-50 dark:bg-blue-900/80';
      case 'completed':
        return 'border-green-500 bg-green-50 dark:bg-green-900/80';
      case 'failed':
        return 'border-red-500 bg-red-50 dark:bg-red-900/80';
      default:
        return 'border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-gray-800';
    }
  };

  const config = data.step?.config as {
    target_queue_id?: number;
    data_source?: 'previous_step_output' | 'specific_step' | 'static';
    write_mode?: 'single' | 'array';
  } | undefined;

  const queueName = queue?.name || (config?.target_queue_id ? `Queue #${config.target_queue_id}` : 'No queue selected');
  const writeMode = config?.write_mode || 'single';
  const dataSource = config?.data_source || 'previous_step_output';

  const handleModelBadgeClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Set selectedNode to this node's ID so only THIS node renders the modal
    setSelectedNode(data.step?.orchestration_step_id || null);
    setIsModelModalOpen(true);
  }, [setSelectedNode, setIsModelModalOpen, data.step?.orchestration_step_id]);

  const handleCloseModelDetails = React.useCallback(() => {
    setIsModelModalOpen(false);
  }, [setIsModelModalOpen]);

  const queueModel = queue?.data_model_id 
    ? models.find(m => m.model_id === queue.data_model_id)
    : undefined;

  return (
    <div className="relative">
      <div
        className={`relative w-72 h-32 px-4 py-3 rounded-lg border-2 shadow-lg transition-all duration-200
          ${selected ? 'ring-2 ring-primary-500 ring-opacity-50' : ''}
          bg-gradient-to-br ${getStatusColor()}
          hover:shadow-xl hover:border-opacity-100`}
      >
        {/* Delete button - only show if node is deletable */}
        {isDeletable && (
          <button
              title="Delete step"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors z-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
        )}

        {/* Target handles for dynamic edge connections */}
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="target"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />

        {/* Queue Write Badge - Similar to other nodes */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-teal-500 to-teal-600 text-white text-sm font-semibold tracking-wide shadow-md whitespace-nowrap z-20">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4" />
            <span>Queue Write</span>
            {data.step?.feedback_loop_hints && Object.keys(data.step.feedback_loop_hints).length > 0 && (
              <Tooltip content="Feedback Loop Hints configured">
                <div>
                  <Sparkles className="w-3 h-3" />
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Main Card Content */}
        <div className="flex flex-col items-center py-2 w-full">
          <div className="flex items-center gap-2 mb-2 w-full justify-center">
            <NodeLabel
              label={isLoadingQueue ? 'Loading...' : queueName}
              maxLength={20}
              fontWeight="medium"
              textColor="text-gray-900 dark:text-warm-white"
            />
            {getStatusIcon()}
            {queue && (
              <Tooltip content="Open queue details">
                <button
                  onClick={handleOpenQueue}
                  className="text-primary-600 hover:text-primary-800 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>

          {/* Queue Info Display */}
          <div className="px-3 py-2 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 text-xs border border-teal-200 dark:border-teal-700 mx-2 self-stretch">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <Database className="w-3 h-3" />
                <span className="font-medium">Mode: {writeMode === 'array' ? 'Array' : 'Single'}</span>
              </div>
              <div className="text-teal-600 dark:text-teal-400">
                {dataSource === 'static' ? 'Static' : dataSource === 'specific_step' ? 'From Step' : 'Previous Output'}
              </div>
            </div>
          </div>

          {/* Model Badge */}
          {queueModel && (
            <NodeBadge
              icon={<Database className="w-3 h-3" />}
              label={queueModel.class_name || 'Model'}
              variant="model"
              onClick={handleModelBadgeClick}
              tooltip="Click to view model details"
              className="mt-1"
            />
          )}
        </div>

        {/* Source handles - Queue write is terminal, but handles are needed for visual consistency */}
        <Handle
          type="source"
          position={Position.Top}
          id="top"
          style={{ top: -12, left: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          style={{ left: -8, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Right}
          id="right"
          style={{ right: -8, top: '50%', transform: 'translate(50%, -50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          style={{ left: '50%', bottom: -12, transform: 'translate(-50%, 50%)', zIndex: 30 }}
          className="w-3 h-3 !bg-primary-500 !border-2 !border-white"
        />
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Step"
          message="Are you sure you want to delete this step? This action cannot be undone."
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={() => {
            handleDelete();
            setShowDeleteConfirm(false);
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Model Details Popup rendered via portal - only render if THIS node opened the modal */}
      {isModelModalOpen && queueModel && selectedNode === data.step?.orchestration_step_id && ReactDOM.createPortal(
        <ModelDetailsModal
          model={queueModel}
          allModels={models}
          onClose={handleCloseModelDetails}
          onEdit={() => {}}
        />,
        document.getElementById('modal-root') as HTMLElement
      )}
    </div>
  );
};

export default QueueWriteNode;
